'use client';

import { c as _c } from "react/compiler-runtime";
import { AnimatePresence, motion } from 'framer-motion';
import { Search } from 'lucide-react';
import { PropsWithChildren, useId } from 'react';
import { cn } from '@/lib/utils';
type AnimatedSearchButtonProps = PropsWithChildren<{
  onClick: () => void;
  isAnimating: boolean;
  isFocused?: boolean;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}>;
const sizeClasses = {
  sm: 'size-4',
  md: 'size-5',
  lg: 'size-6'
};
const iconSizeClasses = {
  sm: 'size-4',
  md: 'size-5',
  lg: 'size-6'
};
export default function AnimatedSearchButton(t0) {
  const $ = _c(40);
  const {
    onClick,
    isAnimating,
    isFocused: t1,
    className,
    size: t2,
    children
  } = t0;
  const isFocused = t1 === undefined ? false : t1;
  const size = t2 === undefined ? "md" : t2;
  const gradId = useId();
  const t3 = sizeClasses[size];
  let t4;
  if ($[0] !== className || $[1] !== t3) {
    t4 = cn("relative", t3, className);
    $[0] = className;
    $[1] = t3;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== isAnimating) {
    t5 = isAnimating && <><motion.div initial={{
        scale: 0,
        opacity: 0
      }} animate={{
        scale: 1.4,
        opacity: 0.08
      }} exit={{
        scale: 1.6,
        opacity: 0
      }} transition={{
        duration: 0.25,
        ease: "easeOut"
      }} className="absolute inset-0 rounded-full bg-gradient-to-r from-[#00B3E3] to-[#C961D9] blur-[1px]" /><motion.div initial={{
        scale: 0,
        opacity: 0
      }} animate={{
        scale: 1.2,
        opacity: 0.15
      }} exit={{
        scale: 1.3,
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: "easeOut",
        delay: 0.02
      }} className="absolute inset-0 rounded-full bg-gradient-to-r from-[#00B3E3] to-[#C961D9]" />{[...Array(6)].map(_temp)}</>;
    $[3] = isAnimating;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== t5) {
    t6 = <AnimatePresence>{t5}</AnimatePresence>;
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== isAnimating) {
    t7 = isAnimating ? {
      scale: [1, 1.05, 1]
    } : {};
    $[7] = isAnimating;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      duration: 0.2,
      ease: "easeInOut"
    };
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const t9 = iconSizeClasses[size];
  let t10;
  if ($[10] !== t9) {
    t10 = cn("stroke-vive-primary-20 stroke-3 transition-all duration-300 hover:opacity-0", t9);
    $[10] = t9;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== t10) {
    t11 = <Search className={t10} />;
    $[12] = t10;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  const t12 = iconSizeClasses[size];
  const t13 = !isFocused;
  let t14;
  if ($[14] !== isFocused || $[15] !== t12 || $[16] !== t13) {
    t14 = cn("absolute inset-0 stroke-3 transition-all duration-300", t12, {
      "opacity-100": isFocused,
      "opacity-0 hover:opacity-100": t13
    });
    $[14] = isFocused;
    $[15] = t12;
    $[16] = t13;
    $[17] = t14;
  } else {
    t14 = $[17];
  }
  const t15 = `url(#${gradId})`;
  let t16;
  if ($[18] !== t15) {
    t16 = {
      stroke: t15
    };
    $[18] = t15;
    $[19] = t16;
  } else {
    t16 = $[19];
  }
  let t17;
  if ($[20] !== t14 || $[21] !== t16) {
    t17 = <Search className={t14} style={t16} />;
    $[20] = t14;
    $[21] = t16;
    $[22] = t17;
  } else {
    t17 = $[22];
  }
  let t18;
  if ($[23] !== t11 || $[24] !== t17 || $[25] !== t7) {
    t18 = <motion.div animate={t7} transition={t8}>{t11}{t17}</motion.div>;
    $[23] = t11;
    $[24] = t17;
    $[25] = t7;
    $[26] = t18;
  } else {
    t18 = $[26];
  }
  let t19;
  if ($[27] !== children || $[28] !== onClick || $[29] !== t18) {
    t19 = <div onClick={onClick} className="cursor-pointer">{t18}{children}</div>;
    $[27] = children;
    $[28] = onClick;
    $[29] = t18;
    $[30] = t19;
  } else {
    t19 = $[30];
  }
  let t20;
  let t21;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <stop offset="0%" stopColor="#00B3E3" />;
    t21 = <stop offset="100%" stopColor="#C961D9" />;
    $[31] = t20;
    $[32] = t21;
  } else {
    t20 = $[31];
    t21 = $[32];
  }
  let t22;
  if ($[33] !== gradId) {
    t22 = <svg width="0" height="0" className="absolute"><defs><linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">{t20}{t21}</linearGradient></defs></svg>;
    $[33] = gradId;
    $[34] = t22;
  } else {
    t22 = $[34];
  }
  let t23;
  if ($[35] !== t19 || $[36] !== t22 || $[37] !== t4 || $[38] !== t6) {
    t23 = <div className={t4}>{t6}{t19}{t22}</div>;
    $[35] = t19;
    $[36] = t22;
    $[37] = t4;
    $[38] = t6;
    $[39] = t23;
  } else {
    t23 = $[39];
  }
  return t23;
}
function _temp(_, i) {
  return <motion.div key={i} initial={{
    scale: 0,
    opacity: 0,
    x: 0,
    y: 0
  }} animate={{
    scale: [0, 0.6, 0],
    opacity: [0, 0.3, 0],
    x: Math.cos(i * 60 * Math.PI / 180) * 8,
    y: Math.sin(i * 60 * Math.PI / 180) * 8
  }} exit={{
    scale: 0,
    opacity: 0
  }} transition={{
    duration: 0.3,
    ease: "easeOut",
    delay: i * 0.03
  }} className="absolute top-1/2 left-1/2 size-0.5 rounded-full bg-gradient-to-r from-[#00B3E3] to-[#C961D9]" style={{
    transform: "translate(-50%, -50%)"
  }} />;
}